import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/memberships/checkout/status/${sessionId}`);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        // continue polling
      }
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success">
      {status === "checking" && (
        <>
          <div className="overline">Confirming</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">Polishing the silverware…</h1>
          <p className="text-[var(--ink-2)] mt-4">Verifying your payment, just a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-20 h-20 rounded-full bg-[var(--emerald)] mx-auto flex items-center justify-center">
            <Check size={36} strokeWidth={1.5} className="text-[var(--gold)]" />
          </div>
          <div className="overline mt-6">Welcome aboard</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">
            You're a <em className="text-[var(--gold)]">{data?.metadata?.tier || "member"}</em> now.
          </h1>
          <p className="text-[var(--ink-2)] mt-4">
            ${(data?.amount_total / 100).toLocaleString()} confirmed. Sasha will guide you through
            a brief intake so your concierge has everything they need.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <button
              onClick={() => navigate("/chat?intake=concierge")}
              className="btn-primary"
              data-testid="success-chat-btn"
            >
              Continue with Sasha → concierge intake
            </button>
            <button onClick={() => navigate("/portal")} className="btn-gold" data-testid="success-portal-btn">
              Open client portal
            </button>
          </div>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <AlertCircle size={48} strokeWidth={1.25} className="text-[var(--burgundy)] mx-auto" />
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">Something interrupted us.</h1>
          <p className="text-[var(--ink-2)] mt-4">
            We couldn't confirm the payment. Try again, and Sasha will be here.
          </p>
          <button onClick={() => navigate("/membership")} className="btn-primary mt-8">
            Back to memberships
          </button>
        </>
      )}
    </div>
  );
}
